import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Sun, Moon, Battery, Box, Home, Newspaper, Lightbulb, Star, PhoneCall, ChevronDown, ChevronUp, Zap, Calculator, X, DollarSign, Anchor, Cpu, Menu, ChevronLeft, ChevronRight, Smartphone, Wrench, Brush, Settings, TrendingUp, Share2, PlusCircle, Store, LayoutDashboard } from 'lucide-react';

const Sidebar = ({ isExpanded, setIsExpanded, closeMobileMenu, isMobileMenuOpen }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentUser, isAdmin } = useAuth();
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isCalculatorsOpen, setIsCalculatorsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isTipsOpen, setIsTipsOpen] = useState(false);
  const [activePath, setActivePath] = useState('/');
  const location = useLocation();
  const navigate = useNavigate();

  // Update active path immediately when location changes
  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);


  const linkClass = (path) => {
    const isActive = activePath === path;
    return `flex items-center p-2 rounded-lg ${isDarkMode ? 'text-blue-100 hover:bg-blue-700' : 'text-amber-100 hover:bg-amber-700'} ${isActive ? 'bg-yellow-400 text-blue-900 font-semibold' : ''} hover:text-yellow-400 transition-colors duration-150 relative z-10 ${!isExpanded ? 'justify-center' : ''}`;
  };

  const dropdownClass = (parentPath) => {
    const isActive = activePath.startsWith(parentPath) && activePath !== parentPath;
    return `flex items-center p-2 rounded-lg ${isDarkMode ? 'text-blue-100 hover:bg-blue-700' : 'text-amber-100 hover:bg-amber-700'} ${isActive ? 'bg-yellow-400 text-blue-900 font-semibold' : ''} hover:text-yellow-400 transition-colors duration-150 relative z-10 ${!isExpanded ? 'justify-center' : ''}`;
  };

  const iconClass = "w-6 h-6";

  const handleLogoClick = (e) => {
    e.preventDefault();
    flushSync(() => {
      setActivePath('/');
    });
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.innerWidth < 1024 && typeof closeMobileMenu === 'function') {
      closeMobileMenu();
    }
  };

  const handleProductsClick = (e) => {
    e.preventDefault();
    if (isExpanded) {
      setIsProductsOpen(!isProductsOpen);
    }
    if (!isProductsOpen || !isExpanded) {
      // Force immediate state update
      flushSync(() => {
        setActivePath('/product/all');
      });
      navigate('/product/all');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (window.innerWidth < 1024) {
        closeMobileMenu();
      }
    }
  };

  const navigateToProducts = () => {
    setActivePath('/product/all');
    navigate('/product/all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.innerWidth < 1024) {
      closeMobileMenu();
    }
    setIsProductsOpen(false);
  };

  const handleCalculatorsClick = (e) => {
    e.preventDefault();
    if (isExpanded) {
      setIsCalculatorsOpen(!isCalculatorsOpen);
    } else {
      setActivePath('/calculators/savings');
      navigate('/calculators/savings');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleServicesClick = (e) => {
    e.preventDefault();
    setActivePath('/services');
    navigate('/services');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (isExpanded) {
      setIsServicesOpen(!isServicesOpen);
    }
    if (window.innerWidth < 1024) {
      closeMobileMenu();
    }
  };

  const handleTipsClick = (e) => {
    e.preventDefault();
    if (isExpanded) {
      setIsTipsOpen(!isTipsOpen);
    } else {
      setActivePath('/social-tips');
      navigate('/social-tips');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (window.innerWidth < 1024) {
      closeMobileMenu();
    }
  };


  return (
    <div className="relative">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          hidden lg:flex items-center justify-center
          absolute top-5 -right-3
          w-6 h-6
          rounded-full
          ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-amber-800 hover:bg-amber-700'}
          text-yellow-400 hover:text-yellow-300
          transition-colors
          z-[110]
          shadow-lg
        `}
        title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {isExpanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>

      <div 
        className={`
          fixed lg:sticky top-0 left-0
          h-screen
          ${isDarkMode ? 'bg-blue-800' : 'bg-amber-900'} 
          text-white 
          overflow-y-auto 
          transition-all
          duration-300 
          ease-in-out 
          custom-scrollbar
          z-[100]
          ${isMobileMenuOpen 
            ? 'w-64 translate-x-0' 
            : 'w-0 lg:w-20 -translate-x-full lg:translate-x-0'
          }
          ${isExpanded ? 'lg:w-64' : 'lg:w-20'}
        `}
      >
        <div className="flex items-center justify-between p-4 border-b border-amber-300">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <div className="flex items-center justify-between w-full">
                 <div className="flex items-center">
                   <button
                     type="button"
                     onClick={handleLogoClick}
                     aria-label="Go to Home"
                     title="Home"
                     className="h-40 w-40 flex-shrink-0 mr-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400/60 hover:opacity-90 transition-opacity"
                   >
                    <svg
                      viewBox="0 0 192 192"
                      className="w-full h-full"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {/* Solar text */}
                      <path d="M46.6,71.49l-6.53,6.39c-2.34-4.88-6.12-7.36-12.24-7.36-5.71,0-8.59,2.41-8.59,5.64,0,3.02,2.47,4.67,10.45,6.12,12.24,2.27,16.84,6.05,16.84,13.61,0,8.52-7.77,13.61-18.63,13.61s-16.15-2.96-20-8.46l5.64-6.74c3.57,5.91,7.91,7.42,14.02,7.42s9.01-1.86,9.01-5.29c0-2.47-2.41-3.92-9.9-5.43-12.65-2.54-17.46-6.39-17.46-13.95,0-8.04,6.81-14.3,18.56-14.3,8.46,0,14.23,2.68,18.84,8.73Z" fill="#facc15"/>
                      <path d="M106.7,97.88c0,3.23.82,4.4,3.37,4.4,1.58,0,2.54-.21,4.06-.76l-.82,6.53c-1.51.89-4.12,1.44-6.39,1.44-6.67,0-9.62-3.09-9.62-10.24v-39.87h9.42v38.5Z" fill="#facc15"/>
                      <path d="M153.17,102.83l-.69,5.71c-1.24.62-2.75.96-4.47.96-4.12,0-6.94-1.44-7.91-5.16-2.06,3.37-6.39,5.16-12.17,5.16-6.53,0-10.66-3.02-10.66-8.8,0-5.36,4.19-9.14,13.4-10.86l8.25-1.58v-1.65c0-3.64-2.13-5.98-5.84-5.98-3.99,0-6.87,1.51-7.91,5.84l-7.49-3.16c1.44-5.84,6.6-9.56,14.99-9.56,9.56,0,15.6,4.12,15.6,12.65v14.09c0,1.79.76,2.61,2.54,2.61.82,0,1.65-.07,2.34-.28ZM138.94,97.61v-3.57l-7.36,1.65c-3.02.69-4.61,1.79-4.61,4.06s1.44,3.37,3.92,3.37c3.57,0,8.04-1.86,8.04-5.5Z" fill="#facc15"/>
                      <path d="M182.04,74.51l-1.17,8.25c-.96-.62-2.54-1.03-4.33-1.03-4.12,0-8.25,3.02-8.25,9.28v17.8h-9.42v-34.37h8.18l.62,6.74c1.79-4.74,5.29-7.42,10.66-7.42,1.51,0,2.89.27,3.71.76Z" fill="#facc15"/>
                      {/* Central solar panel design */}
                      <path d="M95.58,87.97c-.04-.62-.58-1.09-1.21-1.05l-14.88,1.98c-.05-.57-.17-1.12-.34-1.64l14.44-4.12c.59-.21.9-.86.69-1.44s-.86-.9-1.44-.69l-13.8,5.92c-.2-.53-.46-1.03-.77-1.49l12.79-7.86c.51-.36.63-1.07.27-1.58h0c-.36-.51-1.07-.63-1.58-.27l-11.69,9.42c-.33-.45-.72-.86-1.15-1.23l10.2-11.02c.39-.48.32-1.2-.16-1.59h0c-.48-.39-1.2-.32-1.59.16l-8.71,12.23c-.44-.34-.92-.64-1.43-.87l6.85-13.36c.25-.57-.01-1.24-.59-1.49-.57-.25-1.24.01-1.49.59l-5.09,14.12c-.51-.21-1.05-.36-1.62-.45l2.99-14.71c.09-.62-.35-1.19-.97-1.27s-1.19.35-1.27.97l-1.09,14.97c-.28-.03-.56-.05-.84-.05s-.56.02-.84.05l-1.09-14.97c-.09-.62-.66-1.05-1.27-.97s-1.05.66-.97,1.27l2.99,14.71c-.56.09-1.1.24-1.62.45l-5.09-14.12c-.25-.57-.92-.84-1.49-.59h0c-.57.25-.84.92-.59,1.49l6.85,13.36c-.51.24-.99.53-1.43.87l-8.71-12.23c-.39-.48-1.11-.56-1.59-.16h0c-.48.39-.56,1.11-.16,1.59l10.2,11.02c-.43.36-.81.78-1.15,1.23l-11.69-9.42c-.51-.36-1.22-.24-1.58.27-.36.51-.24,1.22.27,1.58l12.79,7.86c-.31.46-.57.96-.77,1.49l-13.8-5.92c-.59-.21-1.24.1-1.44.69s.1,1.24.69,1.44l14.44,4.12c-.17.52-.29,1.08-.34,1.64l-14.88-1.98c-.62-.04-1.16.43-1.21,1.05h0c-.04.62.43,1.16,1.05,1.21l15.01.07c0,.11,0,.22,0,.33,0,.46.04.91.12,1.34l-14.86,2.11c-.61.13-1,.73-.88,1.34s.73,1,1.34.88l14.47-3.99c.13.55.32,1.08.56,1.58l-13.74,6.04c-.55.29-.77.97-.48,1.52.29.55.97.77,1.52.48l12.86-7.74c.27.49.6.95.97,1.37l-11.6,9.53c-.46.43-.48,1.14-.05,1.6.43.46,1.14.48,1.6.05l10.3-10.93c.39.4.83.76,1.3,1.06l-8.6,12.3c-.32.53-.16,1.23.38,1.55h0c.53.32,1.23.16,1.55-.38l6.97-13.3c.48.28,1,.5,1.54.67l-4.96,14.17c-.17.6.18,1.23.78,1.39h0c.6.17,1.23-.18,1.39-.78l3.12-14.68c.23.06.47.11.71.14v15.07h0s0,0,0,0v15.22c0,.65.53,1.17,1.17,1.17.6,0,1.09-.49,1.09-1.09v-30.38c.24-.04.47-.08.71-.14l3.12,14.68c.17.6.79.95,1.39.78.6-.17.95-.79.78-1.39l-4.96-14.17c.54-.17,1.06-.39,1.54-.67l6.97,13.3c.32.53,1.02.7,1.55.38.53-.32.7-1.02.38-1.55l-8.6-12.3c.47-.3.91-.66,1.3-1.06l10.3,10.93c.46.43,1.17.4,1.6-.05.43-.46.4-1.17-.05-1.6l-11.6-9.53c.37-.42.7-.88.97-1.37l12.86,7.74c.55.29,1.24.07,1.52-.48.29-.55.07-1.24-.48-1.52l-13.74-6.04c.24-.5.43-1.03.56-1.58l14.47,3.99c.61.13,1.21-.27,1.34-.88.13-.61-.27-1.21-.88-1.34l-14.86-2.11c.08-.44.12-.88.12-1.34,0-.11,0-.22,0-.33l15.01-.07c.62-.04,1.09-.58,1.05-1.21ZM72.09,95.41c-3.22,0-5.83-2.61-5.83-5.83s2.61-5.83,5.83-5.83,5.83,2.61,5.83,5.83-2.61,5.83-5.83,5.83Z" fill="#facc15"/>
                      {/* Insight.co.za text */}
                      <g fill="#facc15">
                        <path d="M37.52,127.39h-3.35v-16.03h3.35v16.03Z"/>
                        <path d="M52.89,119.43v7.96h-3.33v-6.99c0-2.09-.9-2.77-2.16-2.77-1.41,0-2.79.92-2.79,3.35v6.41h-3.33v-12.14h2.99l.12,2.16c.8-1.63,2.31-2.4,4.13-2.4,2.53,0,4.37,1.48,4.37,4.42Z"/>
                        <path d="M55.39,125.3l1.94-1.99c.68,1.46,1.99,1.89,3.64,1.89,1.43,0,2.23-.44,2.23-1.09,0-.56-.66-.9-2.43-1.26-3.79-.75-5.05-1.92-5.05-3.93s1.87-3.91,5.46-3.91c2.79,0,4.3.83,5.41,2.57l-2.06,1.75c-.7-1.26-1.68-1.89-3.3-1.89-1.36,0-2.19.53-2.19,1.17s.61,1.07,2.65,1.48c3.55.7,4.86,1.77,4.86,3.79,0,2.19-1.92,3.76-5.54,3.76-2.5,0-4.52-.68-5.63-2.33Z"/>
                        <path d="M90.69,128.48c0,2.65-2.72,4.13-6.53,4.13-4.18,0-5.97-1.21-5.97-2.99,0-1.07.73-1.94,2.21-2.31-1-.44-1.8-1.24-1.8-2.43s.78-2.04,1.94-2.45c-1.26-.7-1.92-1.85-1.92-3.3,0-2.55,2.14-4.13,5.58-4.13.9,0,1.72.1,2.43.32.39-1.8,1.51-3.06,3.69-2.96l.44,2.38c-1.53-.15-2.48.34-2.84,1.14,1.19.7,1.85,1.82,1.85,3.25,0,2.53-2.11,4.1-5.56,4.1-.8,0-1.53-.07-2.19-.22-.7.17-.97.53-.97,1.09,0,.66.53,1,1.38,1h4.1c2.62,0,4.15,1.19,4.15,3.38ZM87.97,128.82c0-.63-.44-1.02-1.82-1.02h-3.3c-1.41,0-2.02.39-2.02,1.09,0,.87,1.12,1.43,3.59,1.43s3.55-.68,3.55-1.51ZM81.97,119.11c0,1.24.95,2.04,2.23,2.04s2.21-.8,2.21-2.04-.92-2.02-2.21-2.02-2.23.8-2.23,2.02Z"/>
                        <path d="M104.36,119.43v7.96h-3.33v-6.99c0-2.09-.9-2.77-2.16-2.77-1.41,0-2.79.92-2.79,3.35v6.41h-3.33v-17.46h3.33v7.38c.78-1.55,2.19-2.31,3.91-2.31,2.53,0,4.37,1.48,4.37,4.42Z"/>
                        <path d="M116.02,126.64c-.78.63-2.04,1-3.33,1-2.4,0-4.06-1-4.06-3.67v-6.24h-2.21v-2.48h2.21v-2.79l3.33-.92v3.72h3.93v2.48h-3.93v5.71c0,1.17.63,1.68,1.68,1.68.8,0,1.36-.24,1.87-.68l.51,2.21Z"/>
                        <path d="M121.99,125.64c0,1.21-.83,1.97-2.11,1.97s-2.11-.75-2.11-1.97.8-1.97,2.11-1.97,2.11.75,2.11,1.97Z"/>
                        <path d="M135.73,118.58l-3.11,1.12c-.29-1.46-1.07-2.21-2.36-2.21-1.89,0-2.96,1.31-2.96,3.86s1.14,3.81,2.94,3.81c1.46,0,2.28-.78,2.53-2.16l3.01.97c-.51,2.16-2.4,3.67-5.49,3.67-4.03,0-6.31-2.33-6.31-6.31s2.31-6.31,6.19-6.31c3.42,0,4.98,1.58,5.56,3.57Z"/>
                        <path d="M150.23,121.32c0,3.96-2.31,6.31-6.22,6.31s-6.22-2.36-6.22-6.31,2.31-6.31,6.22-6.31,6.22,2.36,6.22,6.31ZM141.12,121.32c0,2.57,1.02,3.84,2.89,3.84s2.89-1.26,2.89-3.84-1.02-3.84-2.89-3.84-2.89,1.26-2.89,3.84Z"/>
                        <path d="M156.47,125.64c0,1.21-.83,1.97-2.11,1.97s-2.11-.75-2.11-1.97.8-1.97,2.11-1.97,2.11.75,2.11,1.97Z"/>
                        <path d="M158.83,125.26l5.37-6.22,1.38-1.38-2.02.05h-4.69v-2.45h10.39v2.14l-5.37,6.19-1.38,1.41,2.23-.05h4.74v2.45h-10.66v-2.14Z"/>
                        <path d="M184.1,125.28l-.24,2.02c-.44.22-.97.34-1.58.34-1.46,0-2.45-.51-2.79-1.82-.73,1.19-2.26,1.82-4.3,1.82-2.31,0-3.76-1.07-3.76-3.11,0-1.89,1.48-3.23,4.73-3.84l2.91-.56v-.58c0-1.29-.75-2.11-2.06-2.11-1.41,0-2.43.53-2.79,2.06l-2.65-1.12c.51-2.06,2.33-3.38,5.29-3.38,3.38,0,5.51,1.46,5.51,4.47v4.98c0,.63.27.92.9.92.29,0,.58-.02.83-.1ZM179.08,123.43v-1.26l-2.6.58c-1.07.24-1.63.63-1.63,1.43s.51,1.19,1.38,1.19c1.26,0,2.84-.66,2.84-1.94Z"/>
                      </g>
                    </svg>
                   </button>
                </div>
                <button
                  onClick={toggleTheme}
                  className="transition-transform duration-300 hover:rotate-45 p-2 rounded-lg hover:bg-yellow-400/10"
                  title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                >
                  {isDarkMode ? (
                    <Moon size={24} className="text-yellow-400" />
                  ) : (
                    <Sun size={24} className="text-yellow-400" />
                  )}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={handleLogoClick}
                  aria-label="Go to Home"
                  title="Home"
                  className="h-12 w-12 flex-shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400/60 hover:opacity-90 transition-opacity"
                >
                  <svg
                    viewBox="0 0 192 192"
                    className="w-full h-full"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Solar text */}
                    <path d="M46.6,71.49l-6.53,6.39c-2.34-4.88-6.12-7.36-12.24-7.36-5.71,0-8.59,2.41-8.59,5.64,0,3.02,2.47,4.67,10.45,6.12,12.24,2.27,16.84,6.05,16.84,13.61,0,8.52-7.77,13.61-18.63,13.61s-16.15-2.96-20-8.46l5.64-6.74c3.57,5.91,7.91,7.42,14.02,7.42s9.01-1.86,9.01-5.29c0-2.47-2.41-3.92-9.9-5.43-12.65-2.54-17.46-6.39-17.46-13.95,0-8.04,6.81-14.3,18.56-14.3,8.46,0,14.23,2.68,18.84,8.73Z" fill="#facc15"/>
                    <path d="M106.7,97.88c0,3.23.82,4.4,3.37,4.4,1.58,0,2.54-.21,4.06-.76l-.82,6.53c-1.51.89-4.12,1.44-6.39,1.44-6.67,0-9.62-3.09-9.62-10.24v-39.87h9.42v38.5Z" fill="#facc15"/>
                    <path d="M153.17,102.83l-.69,5.71c-1.24.62-2.75.96-4.47.96-4.12,0-6.94-1.44-7.91-5.16-2.06,3.37-6.39,5.16-12.17,5.16-6.53,0-10.66-3.02-10.66-8.8,0-5.36,4.19-9.14,13.4-10.86l8.25-1.58v-1.65c0-3.64-2.13-5.98-5.84-5.98-3.99,0-6.87,1.51-7.91,5.84l-7.49-3.16c1.44-5.84,6.6-9.56,14.99-9.56,9.56,0,15.6,4.12,15.6,12.65v14.09c0,1.79.76,2.61,2.54,2.61.82,0,1.65-.07,2.34-.28ZM138.94,97.61v-3.57l-7.36,1.65c-3.02.69-4.61,1.79-4.61,4.06s1.44,3.37,3.92,3.37c3.57,0,8.04-1.86,8.04-5.5Z" fill="#facc15"/>
                    <path d="M182.04,74.51l-1.17,8.25c-.96-.62-2.54-1.03-4.33-1.03-4.12,0-8.25,3.02-8.25,9.28v17.8h-9.42v-34.37h8.18l.62,6.74c1.79-4.74,5.29-7.42,10.66-7.42,1.51,0,2.89.27,3.71.76Z" fill="#facc15"/>
                    {/* Central solar panel design */}
                    <path d="M95.58,87.97c-.04-.62-.58-1.09-1.21-1.05l-14.88,1.98c-.05-.57-.17-1.12-.34-1.64l14.44-4.12c.59-.21.9-.86.69-1.44s-.86-.9-1.44-.69l-13.8,5.92c-.2-.53-.46-1.03-.77-1.49l12.79-7.86c.51-.36.63-1.07.27-1.58h0c-.36-.51-1.07-.63-1.58-.27l-11.69,9.42c-.33-.45-.72-.86-1.15-1.23l10.2-11.02c.39-.48.32-1.2-.16-1.59h0c-.48-.39-1.2-.32-1.59.16l-8.71,12.23c-.44-.34-.92-.64-1.43-.87l6.85-13.36c.25-.57-.01-1.24-.59-1.49-.57-.25-1.24.01-1.49.59l-5.09,14.12c-.51-.21-1.05-.36-1.62-.45l2.99-14.71c.09-.62-.35-1.19-.97-1.27s-1.19.35-1.27.97l-1.09,14.97c-.28-.03-.56-.05-.84-.05s-.56.02-.84.05l-1.09-14.97c-.09-.62-.66-1.05-1.27-.97s-1.05.66-.97,1.27l2.99,14.71c-.56.09-1.1.24-1.62.45l-5.09-14.12c-.25-.57-.92-.84-1.49-.59h0c-.57.25-.84.92-.59,1.49l6.85,13.36c-.51.24-.99.53-1.43.87l-8.71-12.23c-.39-.48-1.11-.56-1.59-.16h0c-.48.39-.56,1.11-.16,1.59l10.2,11.02c-.43.36-.81.78-1.15,1.23l-11.69-9.42c-.51-.36-1.22-.24-1.58.27-.36.51-.24,1.22.27,1.58l12.79,7.86c-.31.46-.57.96-.77,1.49l-13.8-5.92c-.59-.21-1.24.1-1.44.69s.1,1.24.69,1.44l14.44,4.12c-.17.52-.29,1.08-.34,1.64l-14.88-1.98c-.62-.04-1.16.43-1.21,1.05h0c-.04.62.43,1.16,1.05,1.21l15.01.07c0,.11,0,.22,0,.33,0,.46.04.91.12,1.34l-14.86,2.11c-.61.13-1,.73-.88,1.34s.73,1,1.34.88l14.47-3.99c.13.55.32,1.08.56,1.58l-13.74,6.04c-.55.29-.77.97-.48,1.52.29.55.97.77,1.52.48l12.86-7.74c.27.49.6.95.97,1.37l-11.6,9.53c-.46.43-.48,1.14-.05,1.6.43.46,1.14.48,1.6.05l10.3-10.93c.39.4.83.76,1.3,1.06l-8.6,12.3c-.32.53-.16,1.23.38,1.55h0c.53.32,1.23.16,1.55-.38l6.97-13.3c.48.28,1,.5,1.54.67l-4.96,14.17c-.17.6.18,1.23.78,1.39h0c.6.17,1.23-.18,1.39-.78l3.12-14.68c.23.06.47.11.71.14v15.07h0s0,0,0,0v15.22c0,.65.53,1.17,1.17,1.17.6,0,1.09-.49,1.09-1.09v-30.38c.24-.04.47-.08.71-.14l3.12,14.68c.17.6.79.95,1.39.78.6-.17.95-.79.78-1.39l-4.96-14.17c.54-.17,1.06-.39,1.54-.67l6.97,13.3c.32.53,1.02.7,1.55.38.53-.32.7-1.02.38-1.55l-8.6-12.3c.47-.3.91-.66,1.3-1.06l10.3,10.93c.46.43,1.17.4,1.6-.05.43-.46.4-1.17-.05-1.6l-11.6-9.53c.37-.42.7-.88.97-1.37l12.86,7.74c.55.29,1.24.07,1.52-.48.29-.55.07-1.24-.48-1.52l-13.74-6.04c.24-.5.43-1.03.56-1.58l14.47,3.99c.61.13,1.21-.27,1.34-.88.13-.61-.27-1.21-.88-1.34l-14.86-2.11c.08-.44.12-.88.12-1.34,0-.11,0-.22,0-.33l15.01-.07c.62-.04,1.09-.58,1.05-1.21ZM72.09,95.41c-3.22,0-5.83-2.61-5.83-5.83s2.61-5.83,5.83-5.83,5.83,2.61,5.83,5.83-2.61,5.83-5.83,5.83Z" fill="#facc15"/>
                    {/* Insight.co.za text */}
                    <g fill="#facc15">
                      <path d="M37.52,127.39h-3.35v-16.03h3.35v16.03Z"/>
                      <path d="M52.89,119.43v7.96h-3.33v-6.99c0-2.09-.9-2.77-2.16-2.77-1.41,0-2.79.92-2.79,3.35v6.41h-3.33v-12.14h2.99l.12,2.16c.8-1.63,2.31-2.4,4.13-2.4,2.53,0,4.37,1.48,4.37,4.42Z"/>
                      <path d="M55.39,125.3l1.94-1.99c.68,1.46,1.99,1.89,3.64,1.89,1.43,0,2.23-.44,2.23-1.09,0-.56-.66-.9-2.43-1.26-3.79-.75-5.05-1.92-5.05-3.93s1.87-3.91,5.46-3.91c2.79,0,4.3.83,5.41,2.57l-2.06,1.75c-.7-1.26-1.68-1.89-3.3-1.89-1.36,0-2.19.53-2.19,1.17s.61,1.07,2.65,1.48c3.55.7,4.86,1.77,4.86,3.79,0,2.19-1.92,3.76-5.54,3.76-2.5,0-4.52-.68-5.63-2.33Z"/>
                      <path d="M90.69,128.48c0,2.65-2.72,4.13-6.53,4.13-4.18,0-5.97-1.21-5.97-2.99,0-1.07.73-1.94,2.21-2.31-1-.44-1.8-1.24-1.8-2.43s.78-2.04,1.94-2.45c-1.26-.7-1.92-1.85-1.92-3.3,0-2.55,2.14-4.13,5.58-4.13.9,0,1.72.1,2.43.32.39-1.8,1.51-3.06,3.69-2.96l.44,2.38c-1.53-.15-2.48.34-2.84,1.14,1.19.7,1.85,1.82,1.85,3.25,0,2.53-2.11,4.1-5.56,4.1-.8,0-1.53-.07-2.19-.22-.7.17-.97.53-.97,1.09,0,.66.53,1,1.38,1h4.1c2.62,0,4.15,1.19,4.15,3.38ZM87.97,128.82c0-.63-.44-1.02-1.82-1.02h-3.3c-1.41,0-2.02.39-2.02,1.09,0,.87,1.12,1.43,3.59,1.43s3.55-.68,3.55-1.51ZM81.97,119.11c0,1.24.95,2.04,2.23,2.04s2.21-.8,2.21-2.04-.92-2.02-2.21-2.02-2.23.8-2.23,2.02Z"/>
                      <path d="M104.36,119.43v7.96h-3.33v-6.99c0-2.09-.9-2.77-2.16-2.77-1.41,0-2.79.92-2.79,3.35v6.41h-3.33v-17.46h3.33v7.38c.78-1.55,2.19-2.31,3.91-2.31,2.53,0,4.37,1.48,4.37,4.42Z"/>
                      <path d="M116.02,126.64c-.78.63-2.04,1-3.33,1-2.4,0-4.06-1-4.06-3.67v-6.24h-2.21v-2.48h2.21v-2.79l3.33-.92v3.72h3.93v2.48h-3.93v5.71c0,1.17.63,1.68,1.68,1.68.8,0,1.36-.24,1.87-.68l.51,2.21Z"/>
                      <path d="M121.99,125.64c0,1.21-.83,1.97-2.11,1.97s-2.11-.75-2.11-1.97.8-1.97,2.11-1.97,2.11.75,2.11,1.97Z"/>
                      <path d="M135.73,118.58l-3.11,1.12c-.29-1.46-1.07-2.21-2.36-2.21-1.89,0-2.96,1.31-2.96,3.86s1.14,3.81,2.94,3.81c1.46,0,2.28-.78,2.53-2.16l3.01.97c-.51,2.16-2.4,3.67-5.49,3.67-4.03,0-6.31-2.33-6.31-6.31s2.31-6.31,6.19-6.31c3.42,0,4.98,1.58,5.56,3.57Z"/>
                      <path d="M150.23,121.32c0,3.96-2.31,6.31-6.22,6.31s-6.22-2.36-6.22-6.31,2.31-6.31,6.22-6.31,6.22,2.36,6.22,6.31ZM141.12,121.32c0,2.57,1.02,3.84,2.89,3.84s2.89-1.26,2.89-3.84-1.02-3.84-2.89-3.84-2.89,1.26-2.89,3.84Z"/>
                      <path d="M156.47,125.64c0,1.21-.83,1.97-2.11,1.97s-2.11-.75-2.11-1.97.8-1.97,2.11-1.97,2.11.75,2.11,1.97Z"/>
                      <path d="M158.83,125.26l5.37-6.22,1.38-1.38-2.02.05h-4.69v-2.45h10.39v2.14l-5.37,6.19-1.38,1.41,2.23-.05h4.74v2.45h-10.66v-2.14Z"/>
                      <path d="M184.1,125.28l-.24,2.02c-.44.22-.97.34-1.58.34-1.46,0-2.45-.51-2.79-1.82-.73,1.19-2.26,1.82-4.3,1.82-2.31,0-3.76-1.07-3.76-3.11,0-1.89,1.48-3.23,4.73-3.84l2.91-.56v-.58c0-1.29-.75-2.11-2.06-2.11-1.41,0-2.43.53-2.79,2.06l-2.65-1.12c.51-2.06,2.33-3.38,5.29-3.38,3.38,0,5.51,1.46,5.51,4.47v4.98c0,.63.27.92.9.92.29,0,.58-.02.83-.1ZM179.08,123.43v-1.26l-2.6.58c-1.07.24-1.63.63-1.63,1.43s.51,1.19,1.38,1.19c1.26,0,2.84-.66,2.84-1.94Z"/>
                    </g>
                  </svg>
                </button>
              <button
                onClick={toggleTheme}
                  className="transition-transform duration-300 hover:rotate-45 p-2 rounded-lg hover:bg-yellow-400/10"
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? (
                    <Moon size={20} className="text-yellow-400" />
                ) : (
                    <Sun size={20} className="text-yellow-400" />
                )}
              </button>
              </div>
            )}
          </div>
          
          {isMobileMenuOpen && (
            <button 
              className="lg:hidden text-yellow-400 hover:text-yellow-300"
              onClick={closeMobileMenu}
            >
              <X size={24} />
            </button>
          )}
        </div>
        
        <nav className="mt-4 px-2 flex-1 overflow-y-auto">
          <ul className="space-y-1">
            <SidebarItem 
              to="/" 
              icon={<Home className={iconClass} />} 
              text="Home" 
              isExpanded={isExpanded} 
              linkClass={linkClass}
              closeMobileMenu={closeMobileMenu}
              setActivePath={setActivePath}
            />
            <SidebarItem 
              to="/marketplace" 
              icon={<Store className={iconClass} />} 
              text="Marketplace" 
              isExpanded={isExpanded} 
              linkClass={linkClass}
              closeMobileMenu={closeMobileMenu}
              setActivePath={setActivePath}
            />
            <SidebarDropdown 
              icon={<Box className={iconClass} />} 
              text="Products" 
              isExpanded={isExpanded} 
              isOpen={isProductsOpen} 
              onClick={handleProductsClick}
              onNavigate={navigateToProducts}
              dropdownClass={dropdownClass('/product')}
            >
              <SidebarItem 
                to="/product/panels" 
                icon={<Sun size={16} />} 
                text="Solar Panels" 
                isExpanded={isExpanded} 
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
              <SidebarItem 
                to="/product/batteries" 
                icon={<Battery size={16} />} 
                text="Batteries" 
                isExpanded={isExpanded} 
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
              <SidebarItem to="/products/inverters" icon={<Zap size={16} />} text="Inverters" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
              <SidebarItem to="/product/mounting" icon={<Anchor size={16} />} text="Mounting Systems" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
              <SidebarItem to="/product/controllers" icon={<Cpu size={16} />} text="Charge Controllers" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
            </SidebarDropdown>
            <SidebarDropdown
              icon={<Wrench className={iconClass} />}
              text="Services"
              isExpanded={isExpanded}
              isOpen={isServicesOpen}
              onClick={handleServicesClick}
              dropdownClass={dropdownClass('/services')}
            >
              <SidebarItem
                to="/services/installation"
                icon={<Sun size={16} />}
                text="Solar Installation"
                isExpanded={isExpanded}
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
              <SidebarItem
                to="/services/cleaning"
                icon={<Brush size={16} />}
                text="Solar Cleaning"
                isExpanded={isExpanded}
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
              <SidebarItem
                to="/services/repair"
                icon={<Wrench size={16} />}
                text="Solar Repair"
                isExpanded={isExpanded}
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
              <SidebarItem
                to="/services/maintenance"
                icon={<Settings size={16} />}
                text="Solar Maintenance"
                isExpanded={isExpanded}
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
            </SidebarDropdown>
            <SidebarDropdown 
              icon={<Calculator className={iconClass} />} 
              text="Calculators" 
              isExpanded={isExpanded} 
              isOpen={isCalculatorsOpen} 
              onClick={handleCalculatorsClick}
              dropdownClass={dropdownClass('/calculators')}
            >
              <SidebarItem to="/calculators/savings" icon={<DollarSign size={16} />} text="Savings Calculator" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
              <SidebarItem to="/calculators/cleaning" icon={<Brush size={16} />} text="Cleaning Calculator" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
              <SidebarItem to="/calculators/geyser" icon={<Zap size={16} />} text="Geyser Savings Calculator" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
              <SidebarItem 
                to="/calculators/tilt" 
                icon={<Smartphone size={16} />} 
                text="Panel Tilt Calculator" 
                isExpanded={isExpanded} 
                linkClass={linkClass} 
                closeMobileMenu={closeMobileMenu} 
                setActivePath={setActivePath} 
              />
              <SidebarItem to="/calculators/installation" icon={<Sun size={16} />} text="Installation Calculator" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
            </SidebarDropdown>
            <SidebarItem to="/news" icon={<Newspaper className={iconClass} />} text="News" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
            <SidebarItem to="/insights" icon={<Lightbulb className={iconClass} />} text="Insights" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
            <SidebarItem to="/reviews" icon={<Star className={iconClass} />} text="Reviews" isExpanded={isExpanded} linkClass={linkClass} closeMobileMenu={closeMobileMenu} setActivePath={setActivePath} />
          </ul>
        </nav>

        <div className="mt-auto p-4 border-t border-amber-300 bg-inherit">
          <SidebarDropdown 
            icon={<Share2 className={iconClass} />} 
            text="Social Tips" 
            isExpanded={isExpanded} 
            isOpen={isTipsOpen} 
            onClick={handleTipsClick}
            dropdownClass={dropdownClass('/social-tips')}
          >
            <SidebarItem 
              to="/social-tips" 
              icon={<Sun size={16} />} 
              text="View Tips" 
              isExpanded={isExpanded} 
              linkClass={linkClass}
              closeMobileMenu={closeMobileMenu}
              setActivePath={setActivePath}
            />
            {currentUser && isAdmin && (
              <SidebarItem 
                to="/social-tips/add" 
                icon={<PlusCircle size={16} />} 
                text="Create Tip" 
                isExpanded={isExpanded} 
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
            )}
            {currentUser && isAdmin && (
              <SidebarItem 
                to="/social-tips/manage" 
                icon={<Settings size={16} />} 
                text="Manage Tips" 
                isExpanded={isExpanded} 
                linkClass={linkClass}
                closeMobileMenu={closeMobileMenu}
                setActivePath={setActivePath}
              />
            )}
          </SidebarDropdown>
          <SidebarItem 
            to="/contact" 
            icon={<PhoneCall className={iconClass} />} 
            text="Contact Us" 
            isExpanded={isExpanded} 
            linkClass={linkClass}
            closeMobileMenu={closeMobileMenu}
            setActivePath={setActivePath}
          />
        </div>
      </div>
    </div>
  );
};

const SidebarItem = ({ to, icon, text, isExpanded, linkClass, closeMobileMenu, setActivePath }) => {
  const navigate = useNavigate();
  
  // Validate props for safer execution
  
  const handleClick = (e) => {
    e.preventDefault();
    if (typeof setActivePath === 'function') {
      flushSync(() => {
        setActivePath(to);
      });
    }
    navigate(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.innerWidth < 1024 && typeof closeMobileMenu === 'function') {
      closeMobileMenu();
    }
  };

  // Safe className generation with detailed error handling
  const getClassName = () => {
    try {
      if (typeof linkClass === 'function') {
        return linkClass(to);
      } else {
        return 'flex items-center p-2 rounded-lg text-amber-100 hover:bg-amber-700 hover:text-yellow-400 transition-colors duration-150 relative z-10';
      }
    } catch (error) {
      return 'flex items-center p-2 rounded-lg text-amber-100 hover:bg-amber-700 hover:text-yellow-400 transition-colors duration-150 relative z-10';
    }
  };

  const classNames = getClassName();

  return (
    <li>
      <button 
        onClick={handleClick}
        className={`${classNames} py-2 w-full text-left flex items-center min-w-0 transition-colors duration-200 ${!isExpanded ? 'justify-center px-0' : ''}`}
      >
        <span className="flex-shrink-0" title={!isExpanded ? text : ''}>{icon}</span>
        {isExpanded && (
          <span className="ml-3 truncate flex-1">{text}</span>
        )}
      </button>
    </li>
  );
};

const SidebarDropdown = ({ icon, text, isExpanded, isOpen, onClick, dropdownClass, children }) => (
  <li className="relative">
    <button 
      onClick={onClick} 
      className={`
        w-full py-2 
        ${dropdownClass} 
        flex items-center 
        ${isExpanded ? 'justify-between' : 'justify-center'}
      `}
    >
      <div className={`flex items-center min-w-0 ${!isExpanded ? 'justify-center' : ''}`}>
        <span className="flex-shrink-0" title={!isExpanded ? text : ''}>{icon}</span>
        {isExpanded && <span className="ml-3 truncate">{text}</span>}
      </div>
      {isExpanded && (
        <span className="flex-shrink-0 ml-2">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      )}
    </button>

    {isExpanded && (
      <div className={`
        absolute left-0 w-full bg-inherit
        transition-all duration-300 ease-in-out
        ${isOpen ? 'relative opacity-100 visible' : 'h-0 opacity-0 invisible'}
      `}>
        <ul className={`
          py-1 ml-4 pl-4 
          border-l border-amber-600/30 
          space-y-1
          ${isOpen ? 'mt-1' : ''}
        `}>
          {children}
        </ul>
      </div>
    )}
  </li>
);

export default Sidebar;